import { DefaultDataServiceConfig, EntityCollectionServiceBase, EntityCollectionServiceElementsFactory, EntityCollectionServiceFactory, EntitySelectors$, QueryParams } from 'ngrx-data';
import { Injectable } from '@angular/core';
import { QueryBuilder } from '../utils/query-builder';
import { exhaustMap, flatMap, map, take } from 'rxjs/operators';
import { EntityAuthorizations, IAbstractEntity } from '../interfaces';
import { QueryParser } from '../utils/query-parser';
import { Observable, of, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import * as pluralize from 'pluralize';
import { ucFirst } from '@kaiser-software/chassis-helpers/src/strings';
import { ChassisEntityDispatcherFactory } from '@kaiser-software/chassis-resource/src/services/chassis-entity-dispatcher-base';
import { EntityDispatcher } from 'ngrx-data/dispatchers/entity-dispatcher';

export class ChassisEntityCollectionServiceBase<T extends IAbstractEntity = IAbstractEntity, S$ extends EntitySelectors$<T> = EntitySelectors$<T>> extends EntityCollectionServiceBase<T, S$> {

    public dispatcher: EntityDispatcher<T>;

    public total$: Subject<number> = new Subject();

    protected queryBuilder: QueryBuilder = new QueryBuilder();

    public constructor(entityName: string, serviceElementsFactory: EntityCollectionServiceElementsFactory, protected http: HttpClient, protected config: DefaultDataServiceConfig, protected entityDispatcherFactory: ChassisEntityDispatcherFactory) {
        super(entityName, serviceElementsFactory);

        this.dispatcher = entityDispatcherFactory.create(entityName);
    }

    public where(where: Array<{ attribute: string, operator: string, value: any }> | Function): this ;
    public where(attribute: string, value: any): this ;
    public where(attribute: string, operator: string, value: any): this ;
    public where(...args: any[]): this {
        this.queryBuilder.where.apply(this.queryBuilder, args);
        return this;
    }

    public orWhere(where: Array<{ attribute: string, operator: string, value: any }> | Function): this;
    public orWhere(attribute: string, value: any): this;
    public orWhere(attribute: string, operator: string, value: any): this;
    public orWhere(...args: any[]): this {
        this.queryBuilder.orWhere.apply(this.queryBuilder, arguments);
        return this;
    }

    public whereIn(attribute: string, values: Array<string | number>) {
        this.queryBuilder.whereIn.apply(this.queryBuilder, arguments);
        return this;
    }

    public whereNotIn(attribute: string, values: Array<string | number>) {
        this.queryBuilder.whereNotIn.apply(this.queryBuilder, arguments);
        return this;
    }

    public whereBetween(attribute: string, start: number | string, end: number | string) {
        this.queryBuilder.whereBetween.apply(this.queryBuilder, arguments);
        return this;
    }

    public whereNull(attribute: string) {
        this.queryBuilder.whereNull.apply(this.queryBuilder, arguments);
        return this;
    }

    public whereNotNull(attribute: string) {
        this.queryBuilder.whereNotNull.apply(this.queryBuilder, arguments);
        return this;
    }

    public orderBy(attribute: string, direction: 'asc' | 'desc' = 'asc') {
        this.queryBuilder.orderBy.call(this.queryBuilder, attribute, direction);
        return this;
    }

    public paginate(perPage: number, page: number = 1) {
        this.queryBuilder.paginate.call(this.queryBuilder, perPage, page);
        return this.get();
    }

    public get(): Observable<T[]> {
        const query = this.queryBuilder.getQuery();

        const queryParams = ['perPage', 'page', 'orderBy', 'orderByDirection'];

        const newQuery: QueryParams = queryParams.reduce((acc, param) => {
            if (query[param]) {
                acc[param] = query[param];
            }

            return acc;
        }, {});

        if (query.where.length) {
            const filterValue = [{
                class: 'Kaiser\\Chassis\\Core\\Nova\\Filters\\ChassisResourceFilter',
                value: this.addSlashes(JSON.stringify({where: query.where}))
            }];

            newQuery.filters = btoa(JSON.stringify(filterValue));
        }

        this.getWithQuery(newQuery).subscribe((response: any) => {
            this.total$.next(response.total);
        });

        this.resetCollection();

        return this.entities$.pipe(map(entities => QueryParser.parse(entities, query)));
    }

    public getCached(): Observable<T[]> {
        const query = this.queryBuilder.getQuery();

        const queryParams = ['perPage', 'page', 'orderBy', 'orderByDirection'];

        const newQuery: QueryParams = queryParams.reduce((acc, param) => {
            if (query[param]) {
                acc[param] = query[param];
            }

            return acc;
        }, {});

        if (query.where.length) {
            const filterValue = [{
                class: 'Kaiser\\Chassis\\Core\\Nova\\Filters\\ChassisResourceFilter',
                value: this.addSlashes(JSON.stringify({where: query.where}))
            }];

            newQuery.filters = btoa(JSON.stringify(filterValue));
        }

        return this.entities$.pipe(map(entities => QueryParser.parse(entities, query)))
    }

    public all(): Observable<T[]> {
        this.getAll().subscribe((response: any) => {
            this.total$.next(response.total);
        });

        return this.entities$;
    }

    public find(id: string | number): Observable<T> {
        this.getByKey(id);

        return this.entities$
            .pipe(
                flatMap((entities: Array<T>) => {
                    return entities.filter(entity => entity.id === id);
                })
            );
    }

    public getByKeyCacheFirst(id: number): Observable<T> {
        return this.entityMap$
            .pipe(
                map((entity) => entity[id]),
                exhaustMap((item) => {
                    return item
                        ? of(item)
                        : this.getByKey(id);
                }),
            );
    }

    public getAllCacheFirst(): Observable<Array<T>> {
        return this.entityMap$.pipe(
            exhaustMap(entity => {
                return Object.keys(entity).length
                    ? this.entities$
                    : this.all();
            }),
        );
    }

    public getCreationFields(form: string = null) {
        return this.http.get(this.config.root + '/' + pluralize.plural(this.entityName) + '/creation-fields');
    }

    public getCreationForm() {
        return this.http.get(this.config.root + '/' + pluralize.plural(this.entityName) + '/creation-form');
    }

    public getUpdateFields(id: number | string, form: string = null) {
        return this.http.get(this.config.root + '/' + pluralize.plural(this.entityName) + '/' + id + '/update-fields');
    }

    public getUpdateForm(id: number | string) {
        return this.http.get(this.config.root + '/' + pluralize.plural(this.entityName) + '/' + id + '/update-form');
    }

    /**
     * Check if user is authorized to use the given ability on the entity
     *
     * @param ability
     */
    public authorizedTo(ability: EntityAuthorizations): Observable<boolean> {
        return this.entities$
            .pipe(
                take(1),
                map((entities: T[]) => {
                    const firstEntity = entities.shift();

                    if (firstEntity) {
                        return firstEntity._authorizations['authorizedTo' + ucFirst(ability)];
                    }

                    return false;
                })
            );
    }

    protected addSlashes(str: string) {
        return (str + '').replace(/[\\"']/g, '\\$&').replace(/\u0000/g, '\\0');
    }

    protected resetCollection() {
        this.queryBuilder.reset();
    }
}

@Injectable({
    providedIn: 'root'
})
export class ChassisEntityCollectionServiceFactory extends EntityCollectionServiceFactory {

    public constructor(entityCollectionServiceElementsFactory: EntityCollectionServiceElementsFactory, protected http: HttpClient, protected config: DefaultDataServiceConfig, protected entityDispatcherFactory: ChassisEntityDispatcherFactory) {
        super(entityCollectionServiceElementsFactory);
    }

    /**
     * Create an EntityCollectionService for an entity type
     * @param entityName - name of the entity type
     */
    create<T extends IAbstractEntity = IAbstractEntity, S$ extends EntitySelectors$<T> = EntitySelectors$<T>>(entityName: string): ChassisEntityCollectionServiceBase<T> {
        return new ChassisEntityCollectionServiceBase<T, S$>(entityName, this.entityCollectionServiceElementsFactory, this.http, this.config, this.entityDispatcherFactory);
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-resource/src/services/chassis-entity-collection-service-base.ts